import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { IPaymentRecord } from '@luggagehero/shared/interfaces';
import { SharedIntercomService } from '@luggagehero/shared/services/intercom';
import { SharedPageTaggingService } from '@luggagehero/shared/services/page-tagging';
import { SharedStorageService } from '@luggagehero/shared/services/storage';
import { SharedUserService } from '@luggagehero/shared/services/users';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { cloneDeep } from '@luggagehero/shared/util';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { firstValueFrom, Subscription } from 'rxjs';

import { BaseComponent } from '../../../core';
import { RouterExtensions } from '../../../core/services/index';
import { CurrencyService, StorageManagerService } from '../../../services/index';
import { PayoutVerificationStatus } from '../../../services/storage-manager/payout-verification-status';
import { ManageableStorageLocation } from '../../../services/storage-manager/storage-location';
import {
  PerformanceStats,
  PerformanceStatsResponse,
  StorageLocationAgreement,
} from '../../../services/storage-manager/storage-manager.service';
import { ManagePayoutMethodsBaseComponent } from './manage-payout-methods.base-component';
import { ManageShopsBaseComponent } from './manage-shops.base-component';

interface ProfileTask {
  description: string;
  completed?: boolean;
  action: () => unknown;
}

export enum ReportingPeriod {
  Last7Days = 'LAST_7_DAYS',
  Last2Weeks = 'LAST_2_WEEKS',
  Last4Weeks = 'LAST_4_WEEKS',
  Last3Months = 'LAST_3_MONTHS',
  //Last12Months = 'LAST_12_MONTHS',
  PreviousMonth = 'PREVIOUS_MONTH',
  PreviousQuarter = 'PREVIOUS_QUARTER',
  //PreviousYear = 'PREVIOUS_YEAR',
  MonthToDate = 'MONTH_TO_DATE',
  QuarterToDate = 'QUARTER_TO_DATE',
  YearToDate = 'YEAR_TO_DATE',
  //AllTime = 'ALL_TIME',
}

@Component({ template: '' })
export abstract class ShopManagerBaseComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild('manageShopsComponent') public manageShopsComponent: ManageShopsBaseComponent;
  @ViewChild('managePayoutMethodsComponent') public managePayoutMethodsComponent: ManagePayoutMethodsBaseComponent;
  @ViewChild('tosAcceptanceModal') public tosAcceptanceModal: ModalDirective;
  @ViewChild('phone') public phoneInput: FormControl;
  public visibleProfileTasks: ProfileTask[];
  public isLoadingStats = false;
  public isPhoneNumberFormSubmitted = false;
  public isConfirmationCodeSent = false;
  public phoneNumberForm: FormGroup;
  public phoneCountries = AppConfig.SUPPORTED_PHONE_COUNTRIES;
  public isLineChartEnabled = false;
  public appVersion: string;
  public isInitialized = false;
  public payoutCurrency: string;
  public firstStorageLocationId: string;

  private tosAgreement: StorageLocationAgreement;
  private isTosAgreementStatusReady = false;
  private isStorageLocationInfoReady = false;
  private isPayoutAccountInfoReady = false;
  private isVerificationStatusReady = false;
  private isCurrencyConversionReady = false;
  private storageLocations: ManageableStorageLocation[];
  private _statsPeriod = ReportingPeriod.YearToDate;
  private _stats: PerformanceStatsResponse[];
  private _filterStatsByStorageLocations: string[];
  private _displayedStats: PerformanceStats;
  private _phoneNumber: string;
  private _phoneCountry: string;
  private _isShowingExtendedStats = false;
  private _isReplacingStats = false;
  private _isLoggedIn = false;
  private _isLoading = false;

  private avgValueClickCounter = 0;

  private createAccountTask: ProfileTask;
  private addStorageLocationTask: ProfileTask;
  private acceptTosTask: ProfileTask;
  private addPayoutMethodTask: ProfileTask;
  private verifyBusinessTask: ProfileTask;
  private _profileTasksReady: Promise<boolean>;

  private subscriptions: Subscription[] = [];

  constructor(
    formBuilder: FormBuilder,
    private storageManagerService: StorageManagerService,
    private userService: SharedUserService,
    private currencyService: CurrencyService,
    private router: RouterExtensions,
    private route: ActivatedRoute,
    private intercom: SharedIntercomService,
    private window: SharedWindowService,
    private translate: TranslateService,
    private tag: SharedPageTaggingService,
    private cd: ChangeDetectorRef,
    private sharedStorageService: SharedStorageService,
  ) {
    super();

    this.phoneNumberForm = formBuilder.group({
      // eslint-disable-next-line @typescript-eslint/unbound-method
      phone: ['', [Validators.required, Validators.minLength(6)]],
    });

    // if there is a storageLocationId param in the url save it
    this.route.params.subscribe((params) => {
      // check session
      void this.userService.isLoggedInAsync().then((isLoggedIn) => {
        if (isLoggedIn) {
          if (params['storageLocationId']) {
            const storageLocationId = String(params['storageLocationId']);
            if (storageLocationId && this.userService.user && this.userService.isImpersonationMode()) {
              this.sharedStorageService.storageLocationId = storageLocationId;
            }
          }
        }
      });
    });
  }

  get isLoading(): boolean {
    return this._isLoading;
  }
  set isLoading(value: boolean) {
    this._isLoading = value;
    this.cd.markForCheck();
  }

  get isLoggedIn(): boolean {
    return this._isLoggedIn;
  }
  set isLoggedIn(value: boolean) {
    this._isLoggedIn = value;
    this.cd.markForCheck();
  }

  get isShowingExtendedStats(): boolean {
    return this._isShowingExtendedStats;
  }
  set isShowingExtendedStats(value: boolean) {
    this._isShowingExtendedStats = value;
    this.cd.markForCheck();
  }

  get isReplacingStats(): boolean {
    return this._isReplacingStats;
  }
  set isReplacingStats(value: boolean) {
    this._isReplacingStats = value;
    this.cd.markForCheck();
  }

  get statsPeriod(): ReportingPeriod {
    return this._statsPeriod;
  }
  set statsPeriod(value: ReportingPeriod) {
    this._statsPeriod = value;
    this.cd.markForCheck();

    void this.refreshPerformanceStats();
  }

  get displayedStats(): PerformanceStats {
    return this._displayedStats;
  }

  get isShopOwner(): boolean {
    return this.userService.user.isShopOwner;
  }

  get isImpersonationMode(): boolean {
    return this.userService.isImpersonationMode();
  }

  get profileTasksReady(): Promise<boolean> {
    if (this._profileTasksReady == null) {
      void this.initializeProfileTasks();
    }
    return this._profileTasksReady;
  }

  get profileTaskCount(): number {
    if (!this.visibleProfileTasks) {
      return 0;
    }
    return this.visibleProfileTasks.length;
  }

  get completedTaskCount(): number {
    if (!this.visibleProfileTasks) {
      return 0;
    }
    return this.visibleProfileTasks.filter((t) => t.completed).length;
  }

  get nextIncompleteTask(): ProfileTask {
    if (!this.visibleProfileTasks) {
      return undefined;
    }
    return this.visibleProfileTasks.filter((t) => !t.completed)[0];
  }

  get showAddStorageLocationAlert(): boolean {
    if (!AppConfig.IS_STORAGE_MANAGER_DASHBOARD_ALERTS_ENABLED) {
      return false;
    }
    return this.nextIncompleteTask === this.addStorageLocationTask;
  }

  get showTosAlert(): boolean {
    if (!AppConfig.IS_STORAGE_MANAGER_DASHBOARD_ALERTS_ENABLED) {
      return false;
    }
    return this.nextIncompleteTask === this.acceptTosTask;
  }

  get showPayoutMethodAlert(): boolean {
    if (!AppConfig.IS_STORAGE_MANAGER_DASHBOARD_ALERTS_ENABLED) {
      return false;
    }
    return this.nextIncompleteTask === this.addPayoutMethodTask;
  }

  get showVerifyBusinessAlert(): boolean {
    if (!AppConfig.IS_STORAGE_MANAGER_DASHBOARD_ALERTS_ENABLED) {
      return false;
    }
    return this.nextIncompleteTask === this.verifyBusinessTask;
  }

  get showAccountCompleteness(): boolean {
    if (!AppConfig.IS_STORAGE_MANAGER_PROFILE_COMPLETENESS_ENABLED) {
      return false;
    }
    return this.completedTaskCount > 0;
  }

  get showActivityStats(): boolean {
    if (!AppConfig.IS_STORAGE_MANAGER_ACTIVITY_STATS_ENABLED) {
      return false;
    }
    return true;
  }

  get activityStatsValid(): boolean {
    if (!this.displayedStats) {
      return false;
    }
    if (!this.displayedStats.totalPaidBookings) {
      return false;
    }
    if (!this._displayedStats.totalRevenue || this._displayedStats.totalRevenue < 0) {
      return false;
    }
    return true;
  }

  get reportingPeriodOptions(): string[] {
    if (!this.storageLocations || this.storageLocations.length === 0) {
      return [];
    }

    let options: ReportingPeriod[] = Object.values(ReportingPeriod);

    // Hide "Last 2 weeks" option for now
    options = options.filter((p) => p !== ReportingPeriod.Last2Weeks);

    switch (this.operatingCountry.toLowerCase()) {
      //
      // For DK and GB, eliminate 'All time' for now as we had a change in currency which would cause the reported
      // numbers to be inaccurate. For Denmark the change happened late 2021 (EUR => DKK) and for GB it happened late
      // 2019 (EUR => GBP). This can be removed once we add support for currency conversion on the server-side during
      // the calculation of the stats.
      //
      case 'dk':
      case 'gb':
      case 'uk': {
        //options = options.filter((p) => p !== ReportingPeriod.AllTime);
        break;
      }

      default: {
        if (this.storageLocations.find((s) => AppConfig.SHOPS_WITH_ALL_TIME_REPORTING_DISABLED.includes(s._id))) {
          // Remove 'All time' for storage locations where this is explicitly configured
          //options = options.filter((p) => p !== ReportingPeriod.AllTime);
        }
        break;
      }
    }

    return options;
  }

  get isLookbackPeriodSelected() {
    switch (this.statsPeriod) {
      case ReportingPeriod.PreviousMonth:
      case ReportingPeriod.PreviousQuarter:
        //case ReportingPeriod.PreviousYear:
        return false;

      default:
        return true;
    }
  }

  get startDate(): string {
    switch (this.statsPeriod) {
      case ReportingPeriod.PreviousMonth: {
        const m = moment().subtract(1, 'month').startOf('month');
        return m.format('YYYY-MM-DD');
      }

      case ReportingPeriod.PreviousQuarter: {
        const m = moment().subtract(1, 'quarter').startOf('quarter');
        return m.format('YYYY-MM-DD');
      }

      // case ReportingPeriod.PreviousYear: {
      //   const m = moment().subtract(1, 'year').startOf('year');
      //   return m.format('YYYY-MM-DD');
      // }

      default: {
        const m = moment().subtract(this.reportingDays, 'days');
        return m.format('YYYY-MM-DD');
      }
    }
  }

  get endDate(): string {
    switch (this.statsPeriod) {
      case ReportingPeriod.PreviousMonth: {
        const m = moment().subtract(1, 'month').endOf('month');
        return m.format('YYYY-MM-DD');
      }

      case ReportingPeriod.PreviousQuarter: {
        const m = moment().subtract(1, 'quarter').endOf('quarter');
        return m.format('YYYY-MM-DD');
      }

      // case ReportingPeriod.PreviousYear: {
      //   const m = moment().subtract(1, 'year').endOf('year');
      //   return m.format('YYYY-MM-DD');
      // }

      default: {
        return moment().format('YYYY-MM-DD');
      }
    }
  }

  get tosLink(): string {
    return this.tosAgreement && this.tosAgreement.agreementUrl;
  }

  get storageManagerPhoneNumber(): string {
    if (!this.isLoggedIn || !this.userService.user) {
      return '';
    }
    return this.userService.user.phone;
  }

  get storageManagerPhoneNumberLast4(): string {
    const phoneNumber = this.storageManagerPhoneNumber;
    if (!phoneNumber) {
      return '';
    }
    const last4 = phoneNumber.slice(phoneNumber.length - 4);
    return last4;
  }

  get phoneNumber(): string {
    return this._phoneNumber;
  }
  set phoneNumber(value: string) {
    this._phoneNumber = value;
    this.cd.markForCheck();
  }

  get phoneCountry(): string {
    return this._phoneCountry;
  }
  set phoneCountry(value: string) {
    this._phoneCountry = value;
    this.cd.markForCheck();
  }

  get phoneInputError(): string {
    if (!this.isPhoneNumberFormSubmitted) {
      return undefined;
    }
    const phoneInput = this.phoneNumberForm.get('phone');
    if (!phoneInput.errors) {
      return undefined;
    }
    if (phoneInput.errors.required) {
      return 'PHONE_NUMBER_MISSING';
    }
    if (phoneInput.errors.minlength) {
      return 'PHONE_NUMBER_TOO_SHORT';
    }
    if (phoneInput.errors.invalid) {
      return (phoneInput.errors.invalid as { reason?: string })?.reason;
    }
    return 'INVALID_PHONE_NUMBER';
  }

  get contractSigned(): boolean {
    return this.storageLocations && this.storageLocations.some((s) => s.contractSigned);
  }

  get operatingCountry(): string {
    if (!this.storageLocations?.length) {
      // No storage locations added yet
      return '';
    }
    return this.storageLocations[0].address?.countryCode;
  }

  async ngOnInit() {
    this.tag.disableCrawling();
    this.subscriptions.push(this.translate.onLangChange.subscribe(() => void this.initializeProfileTasks()));
    this.subscriptions.push(this.userService.loginAnnounced.subscribe((e) => (this.isLoggedIn = e.isLoggedIn)));

    this.isLoading = true;

    this.appVersion = this.window.appVersion;

    await Promise.allSettled([
      this.currencyService.init().then(() => (this.isCurrencyConversionReady = true)),
      this.updateTosAgreementStatus(),
      this.initializeProfileTasks(),
    ]);

    this.checkIfAllInfoIsReady();
  }

  ngOnDestroy() {
    this.tag.clearTags();
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  showChat() {
    this.intercom.show();
  }

  async onShopsChange(shops: ManageableStorageLocation[]) {
    if (shops && shops.length > 0) {
      // One or more storage locations have been added
      await this.completeTask(this.addStorageLocationTask);

      this.storageLocations = shops;
      if (this.storageLocations && this.storageLocations.length > 0) {
        this.firstStorageLocationId = shops[0]._id;
      }
      this.payoutCurrency = await this.storageManagerService.getDefaultPayoutCurrency(this.operatingCountry);

      await this.updateTosAgreementStatus();
      this.updateVisibleProfileTasks();

      if (this.userService.user.isShopOwner) {
        await this.refreshPerformanceStats();
      }
    } else {
      // No storage locations have been added
      this.addStorageLocationTask.completed = false;
    }

    this.isStorageLocationInfoReady = true;
    this.checkIfAllInfoIsReady();
  }

  onPayoutMethodsChange(payoutMethods: IPaymentRecord[]) {
    this.addPayoutMethodTask.completed = payoutMethods[0]?.data ? true : false;
    this.updateVisibleProfileTasks();

    this.isPayoutAccountInfoReady = true;
    this.checkIfAllInfoIsReady();
  }

  onPayoutVerificationStatusChange(status: PayoutVerificationStatus) {
    this.verifyBusinessTask.completed = status && status.code === 'Successful';

    this.isVerificationStatusReady = true;
    this.checkIfAllInfoIsReady();
  }

  async startTosAcceptance() {
    if (this.storageManagerPhoneNumber) {
      this.isLoading = true;

      try {
        await this.requestTosAcceptance();
      } catch (err) {
        // TODO: Notify user
      }
      this.isLoading = false;
    }
    this.tosAcceptanceModal.show();
  }

  async savePhoneNumber() {
    this.isPhoneNumberFormSubmitted = true;

    if (this.phoneInputError) {
      return;
    }
    this.isLoading = true;

    try {
      // Set phone number on user object
      const currentUser = this.userService.user;
      currentUser.phone = this.phoneNumber;
      currentUser.phoneCountry = this.phoneCountry;

      // Update the user via the API
      await this.userService.updateUser(currentUser);

      // Request one-time code to be sent to the user by SMS
      await this.requestTosAcceptance();
    } catch (err) {
      // TODO: Notify user of error
    }

    this.isLoading = false;
  }

  async resendConfirmationCode() {
    this.isConfirmationCodeSent = false;
    this.isLoading = true;

    await this.requestTosAcceptance();

    this.isConfirmationCodeSent = true;
    this.isLoading = false;
  }

  async requestTosAcceptance() {
    try {
      await this.storageManagerService.requestAcceptTermsOfService(this.tosAgreement._id);
    } catch (err) {
      // TODO: Handle errors
    }
  }

  async confirmTosAcceptance(code: string) {
    try {
      const agreement = await this.storageManagerService.confirmAcceptTermsOfService(this.tosAgreement._id, code);
      await this.updateTosAgreementStatus(agreement);

      this.tosAcceptanceModal.hide();
    } catch (err) {
      // TODO: Display error on failure
    }

    // TODO: Display error on failure
  }

  async updateTosAgreementStatus(agreement?: StorageLocationAgreement) {
    this.tosAgreement = agreement || (await this.storageManagerService.getLatestTermsOfService());

    if (this.tosAgreement && this.tosAgreement.signed) {
      await this.completeTask(this.acceptTosTask);
    }
    this.isTosAgreementStatusReady = true;
    this.cd.markForCheck();
  }

  async onCodeCompleted(code: string) {
    this.isLoading = true;
    await this.confirmTosAcceptance(code);
    this.isLoading = false;
  }

  onAverageValueClick() {
    this.avgValueClickCounter++;

    if (this.avgValueClickCounter >= 3) {
      this.isShowingExtendedStats = !this.isShowingExtendedStats;
      this.avgValueClickCounter = 0;
    }
  }

  private async refreshPerformanceStats(): Promise<void> {
    this.isLoadingStats = true;

    const minDelay = new Promise<void>((resolve) => setTimeout(() => resolve(), 500));

    const results: PerformanceStatsResponse[] = [];

    for (const storageLocation of this.storageLocations) {
      try {
        const res = await (this.isLookbackPeriodSelected
          ? this.storageManagerService.getPerformance(storageLocation._id, this.reportingDays)
          : this.storageManagerService.getPerformanceByPeriod(storageLocation._id, this.startDate, this.endDate));
        results.push(res);
      } catch {
        // Ignore errors for now; will display 'no data available' message to the user
      }
    }

    await minDelay;

    this._stats = results;

    void this.updateVisiblePerformanceStats();

    this.isLoadingStats = false;
    this.cd.markForCheck();
  }

  private async initializeProfileTasks() {
    if (this._profileTasksReady == null) {
      // The first time, create a promise that can be used by others to wait for profile tasks to be initialized
      this._profileTasksReady = this.loadProfileTasks().then(() => true);
      await this._profileTasksReady;
    } else {
      // For subsequent calls just reload the tasks
      await this.loadProfileTasks();
    }
  }

  private async loadProfileTasks() {
    const translationValues = this.translate.get([
      'CREATE_YOUR_ACCOUNT',
      'ADD_STORAGE_LOCATION',
      'ACCEPT_TERMS_OF_SERVICE',
      'SET_UP_A_PAYOUT_METHOD',
      'VERIFY_YOUR_BUSINESS',
    ]);
    const translations = (await firstValueFrom<unknown>(translationValues)) as {
      CREATE_YOUR_ACCOUNT: string;
      ADD_STORAGE_LOCATION: string;
      ACCEPT_TERMS_OF_SERVICE: string;
      SET_UP_A_PAYOUT_METHOD: string;
      VERIFY_YOUR_BUSINESS: string;
    };

    this.createAccountTask = {
      description: translations['CREATE_YOUR_ACCOUNT'],
      action: null,
      completed: true,
    };
    this.addStorageLocationTask = {
      description: translations['ADD_STORAGE_LOCATION'],
      action: () => this.manageShopsComponent.addShop(),
      completed: this.addStorageLocationTask?.completed || false,
    };
    this.acceptTosTask = {
      description: translations['ACCEPT_TERMS_OF_SERVICE'],
      action: () => this.router.navigate(['terms-of-service']),
      completed: this.acceptTosTask?.completed || false,
    };
    this.addPayoutMethodTask = {
      description: translations['SET_UP_A_PAYOUT_METHOD'],
      action: () => this.managePayoutMethodsComponent.addPayoutProviderAccount(),
      completed: this.addPayoutMethodTask?.completed || false,
    };
    this.verifyBusinessTask = {
      description: translations['VERIFY_YOUR_BUSINESS'],
      action: () => this.managePayoutMethodsComponent.managePayoutProviderAccount(),
      completed: this.verifyBusinessTask?.completed || false,
    };

    this.updateVisibleProfileTasks();
  }

  private async updateVisiblePerformanceStats() {
    this.isReplacingStats = true;

    let visibleStats = cloneDeep(this._stats);

    if (this._filterStatsByStorageLocations && this._filterStatsByStorageLocations.length > 0) {
      visibleStats = visibleStats.filter((s) => this._filterStatsByStorageLocations.includes(s.shopId));
    }

    this._displayedStats = this.summarizeStats(visibleStats);

    // HACK: For the chart elements to be removed and re-added to the DOM in order to trigger animation
    await new Promise((resolve) => setTimeout(() => resolve((this.isReplacingStats = false)), 0));
  }

  private async completeTask(task: ProfileTask) {
    await this.profileTasksReady;
    task.completed = true;
  }

  private summarizeStats(stats: PerformanceStatsResponse[]): PerformanceStats {
    // Use currency of the first stats object or default to euro
    const currency = stats.length > 0 && stats[0] && stats[0].currency ? stats[0].currency : 'eur';

    const summarizedStats: PerformanceStats = {
      totalPaidBookings: 0,
      totalBagsStored: 0,
      totalRevenue: 0,
      totalTips: 0,
      averageValuePerBooking: 0,
      averageValuePerBag: 0,
      currency,
    };

    for (const stat of stats) {
      if (!stat) {
        continue;
      }
      summarizedStats.totalPaidBookings += stat.totalPaidBookings || 0;
      summarizedStats.totalBagsStored += stat.totalBagsStored || 0;
      summarizedStats.totalRevenue += this.currencyService.convert(
        stat.totalRevenue,
        stat.currency,
        summarizedStats.currency,
      );
      summarizedStats.totalTips += this.currencyService.convert(
        stat.totalTips,
        stat.currency,
        summarizedStats.currency,
      );
    }

    summarizedStats.averageValuePerBooking =
      summarizedStats.totalPaidBookings > 0
        ? (summarizedStats.totalRevenue + summarizedStats.totalTips) / summarizedStats.totalPaidBookings
        : 0;
    summarizedStats.averageValuePerBag =
      summarizedStats.totalBagsStored > 0
        ? (summarizedStats.totalRevenue + summarizedStats.totalTips) / summarizedStats.totalBagsStored
        : 0;

    return summarizedStats;
  }

  private get reportingDays(): number {
    const startDate = moment();

    switch (this.statsPeriod) {
      case ReportingPeriod.Last7Days:
        startDate.subtract(7, 'days');
        break;

      case ReportingPeriod.Last2Weeks:
        startDate.subtract(2, 'weeks');
        break;

      case ReportingPeriod.Last4Weeks:
        startDate.subtract(4, 'weeks');
        break;

      case ReportingPeriod.Last3Months:
        startDate.subtract(3, 'months');
        break;

      // case ReportingPeriod.Last12Months:
      //   startDate.subtract(12, 'months');
      //   break;

      case ReportingPeriod.MonthToDate:
        startDate.startOf('month');
        break;

      case ReportingPeriod.QuarterToDate:
        startDate.startOf('quarter');
        break;

      case ReportingPeriod.YearToDate:
        startDate.startOf('year');
        break;

      // case ReportingPeriod.AllTime:
      //   // Go back to LuggageHero launch date (April, 2017)
      //   startDate = moment('2017-04-01', 'YYYY-MM-DD');
      //   break;
    }

    const numberOfDays = moment().diff(startDate, 'days');

    return numberOfDays;
  }

  private checkIfAllInfoIsReady() {
    if (
      this.isTosAgreementStatusReady &&
      this.isStorageLocationInfoReady &&
      this.isPayoutAccountInfoReady &&
      this.isVerificationStatusReady &&
      this.isCurrencyConversionReady
    ) {
      this.isLoading = false;
    }
  }

  private updateVisibleProfileTasks() {
    this.visibleProfileTasks = [];

    this.visibleProfileTasks.push(this.createAccountTask);
    this.visibleProfileTasks.push(this.addStorageLocationTask);

    if (this.tosAgreement && !this.contractSigned) {
      this.visibleProfileTasks.push(this.acceptTosTask);
    }

    this.visibleProfileTasks.push(this.addPayoutMethodTask);

    if (this.addPayoutMethodTask.completed) {
      this.visibleProfileTasks.push(this.verifyBusinessTask);
    }

    this.cd.markForCheck();
  }
}
